import type { SiteDomain, SiteSeo } from "../api/contract";
import { languageName, zoneCity } from "./siteLocale";

/** The groups of the Site settings modal, in the order they are listed. */
export type SettingsGroup = "address" | "search" | "language" | "plan" | "analytics";

export const SETTINGS_GROUPS: ReadonlyArray<{ id: SettingsGroup; label: string; lede: string }> = [
  { id: "address", label: "Address", lede: "Where people find your site." },
  { id: "search", label: "Search and sharing", lede: "How your site looks in search results, browser tabs and shared links." },
  { id: "language", label: "Language and time", lede: "The language and clock your whole site goes by." },
  { id: "plan", label: "Plan", lede: "What you can do, and what it costs." },
  { id: "analytics", label: "Analytics", lede: "Count the visits to your published site." },
];

/** The groups whose fields live in the one Site details form (the rest save on their own). */
export const DETAILS_GROUPS: readonly SettingsGroup[] = ["search", "language", "analytics"];

type SeoSummary = Pick<SiteSeo, "title" | "image" | "language" | "timeZone" | "analytics">;

/** The site's address: its own domain once that is connected and verified, else the Stack.je host. */
export function addressValue(input: { domain?: Pick<SiteDomain, "hostname" | "status"> | null; platformAddress: string }): string {
  return input.domain?.status === "active" ? input.domain.hostname : input.platformAddress;
}

export function searchValue(seo: Pick<SeoSummary, "title" | "image"> | undefined): string {
  const title = Boolean(seo?.title?.trim());
  const image = Boolean(seo?.image);
  if (title && image) return "Title and image set";
  if (title) return "Title set, no share image";
  return image ? "Share image set, no title" : "Not set yet";
}

/** "English · Singapore time", the parts that are set, or "Not set". */
export function languageValue(seo: Pick<SeoSummary, "language" | "timeZone"> | undefined): string {
  const tag = seo?.language?.trim();
  const zone = seo?.timeZone?.trim();
  const parts = [
    tag ? languageName(tag.split("-")[0]!) : null,
    zone ? (zone === "UTC" ? "UTC" : `${zoneCity(zone)} time`) : null,
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(" · ") : "Not set";
}

/** `undefined` while the plan is still being read. */
export function planValue(entitled: boolean | undefined): string {
  return entitled === undefined ? "Checking…" : entitled ? "Pro" : "Free";
}

export function analyticsValue(seo: Pick<SeoSummary, "analytics"> | undefined): string {
  return seo?.analytics?.trim() ? "On" : "Off";
}

/** What each nav row shows under its label, so the nav doubles as a summary of the site's state. */
export function settingsValues(input: { seo?: SeoSummary; domain?: Pick<SiteDomain, "hostname" | "status"> | null; platformAddress: string; entitled?: boolean }): Record<SettingsGroup, string> {
  return {
    address: addressValue(input),
    search: searchValue(input.seo),
    language: languageValue(input.seo),
    plan: planValue(input.entitled),
    analytics: analyticsValue(input.seo),
  };
}

/** The switcher row's subtitle: "tanah.stack.je · Free". */
export function settingsSubtitle(address: string, entitled: boolean | undefined): string {
  return entitled === undefined ? address : `${address} · ${planValue(entitled)}`;
}
